// Local-mode mocks for password rotation (POST /passwords/*) and password SSH
// exceptions (POST /instances/passwordSsh, /instances/setPasswordSsh).
// Stateful in memory: rotations and reveals change what the lists return
// until reload. Never a real password.
//
// Seed (the backend dev's test data, on web-01.prod = instance 1): profile
// "web-vm-01 test" (gateway logs in with the password) manages alice and bob
// (rotated); alice is pinned to the password and uses it; carol and sshtest
// have no password; deploy failed; svc-backup waits for its first rotation.
// A second profile rotates root for break-glass only.

import { SSH_PROXY_MOCKS } from "./sshProxyMocks";

const MIN = 60e3;
const DAY = 24 * 60 * MIN;
const ago = (ms) => new Date(Date.now() - ms).toISOString();
const ahead = (ms) => new Date(Date.now() + ms).toISOString();

const hostOf = (id) =>
  (SSH_PROXY_MOCKS["/instances/list"]?.instances || []).find((r) => r.instanceId === Number(id))?.hostName || "";

let seq = 100;
const uuid = () => {
  seq += 1;
  return `7a3e9c10-0000-4000-8000-${String(seq).padStart(12, "0")}`;
};

const PROFILE_WEB = "7a3e9c10-0000-4000-8000-000000000001";
const PROFILE_ROOT = "7a3e9c10-0000-4000-8000-000000000002";

const profiles = [
  {
    id: PROFILE_WEB, name: "web-vm-01 test", status: "active", endpointIds: [1],
    accounts: ["alice", "bob", "carol", "sshtest", "deploy", "svc-backup"], includeRoot: false,
    intervalDays: 30, length: 24, upper: true, lower: true, digits: true, special: true,
    rerotateAfterRevealMinutes: 60, loginMethod: "password", createdBy: 4,
    createdAt: ago(12 * DAY), updatedAt: ago(2 * DAY),
  },
  {
    id: PROFILE_ROOT, name: "Root break-glass", status: "active", endpointIds: [1],
    accounts: [], includeRoot: true,
    intervalDays: 7, length: 32, upper: true, lower: true, digits: true, special: false,
    rerotateAfterRevealMinutes: 30, loginMethod: "none", createdBy: 4,
    createdAt: ago(10 * DAY), updatedAt: ago(10 * DAY),
  },
];

const PASSWORD_SSH = {
  1: { state: "gateway-only, except ubuntu", allow: ["ubuntu"], needsSetup: false },
};
const passwordSshOf = (id) => PASSWORD_SSH[Number(id)] || { state: "", allow: [], needsSetup: true };

const row = (endpointId, account, policyId, extra) => ({
  endpointId, hostname: hostOf(endpointId), account, policyId, status: "pending", detail: "", failures: 0,
  nextDueAt: ago(5 * MIN), version: 0, unconfirmed: false, needsSetup: false, jobRunning: false,
  credentialOverride: "", rotationJob: "", updatedAt: ago(5 * MIN),
  ...extra,
});

const accounts = [
  row(1, "alice", PROFILE_WEB, {
    status: "managed", rotatedAt: ago(3 * DAY), nextDueAt: ahead(27 * DAY), version: 4,
    revealedAt: ago(4 * DAY), revealedBy: 4, credentialOverride: "password", rotationJob: "job-a1",
  }),
  row(1, "bob", PROFILE_WEB, { status: "managed", rotatedAt: ago(3 * DAY), nextDueAt: ahead(27 * DAY), version: 2, rotationJob: "job-b1" }),
  row(1, "carol", PROFILE_WEB, {
    status: "no_password", detail: "the account has no password set; left locked", nextDueAt: ahead(27 * DAY),
  }),
  row(1, "sshtest", PROFILE_WEB, {
    status: "no_password", detail: "the account has no password set; left locked", nextDueAt: ahead(27 * DAY),
  }),
  row(1, "deploy", PROFILE_WEB, {
    status: "failed", detail: "chpasswd: exit status 1: Authentication token manipulation error", failures: 2,
    rotatedAt: ago(40 * DAY), nextDueAt: ahead(3 * 60 * MIN), version: 1, unconfirmed: true,
  }),
  row(1, "svc-backup", PROFILE_WEB, {}),
  row(1, "root", PROFILE_ROOT, { status: "managed", rotatedAt: ago(1 * DAY), nextDueAt: ahead(6 * DAY), version: 3 }),
];

// The derived fields, as ListAccounts computes them.
function derive(a) {
  const p = profiles.find((x) => x.id === a.policyId);
  const out = { ...a };
  delete out._doneAt;
  const credential = !p || p.status !== "active" ? "key" : a.credentialOverride || (p.loginMethod === "password" ? "password" : "key");
  return {
    ...out,
    hostname: a.hostname || hostOf(a.endpointId),
    policyName: p?.name || "",
    loginMethod: p?.loginMethod || "",
    credential,
    inUse: credential === "password" && a.version > 0 ? "password" : "key",
    revealable: a.version > 0 || a.unconfirmed,
    passwordSsh: passwordSshOf(a.endpointId).state,
  };
}

// Rotations queued by Rotate now finish a few seconds later.
function settleJobs() {
  const now = Date.now();
  accounts.forEach((a) => {
    if (!a.jobRunning || now < a._doneAt) return;
    const p = profiles.find((x) => x.id === a.policyId);
    const next = ahead((p?.intervalDays || 30) * DAY);
    a.jobRunning = false;
    a._doneAt = undefined;
    a.updatedAt = new Date().toISOString();
    if (a.status === "no_password" && a.account !== "root") {
      a.nextDueAt = next;
      return;
    }
    Object.assign(a, { status: "managed", detail: "", failures: 0, rotatedAt: new Date().toISOString(), nextDueAt: next, rerotateAt: undefined, version: a.version + 1, unconfirmed: false });
  });
}

const reveals = {};

export const PASSWORD_MOCKS = [
  {
    pattern: "/passwords/listaccounts",
    respond: (body) => {
      settleJobs();
      const ep = Number(body?.instanceId) || 0;
      const pid = String(body?.policyId || "");
      const list = accounts
        .filter((a) => (!ep || a.endpointId === ep) && (!pid || a.policyId === pid))
        .map(derive)
        .sort((a, b) => `${a.hostname}/${a.account}`.localeCompare(`${b.hostname}/${b.account}`));
      return { accounts: list };
    },
  },
  {
    pattern: "/passwords/rotatenow",
    respond: (body) => {
      const a = accounts.find((x) => x.endpointId === Number(body?.endpointId) && x.account === body?.account);
      const jobId = `job-${Date.now().toString(36)}`;
      if (a) Object.assign(a, { jobRunning: true, rotationJob: jobId, _doneAt: Date.now() + 8000 });
      return { jobId };
    },
  },
  {
    pattern: "/passwords/revealstart",
    respond: (body) => {
      const revealId = uuid();
      reveals[revealId] = { endpointId: Number(body?.endpointId), account: body?.account, polls: 0, status: "pending" };
      return { revealId, waitSeconds: 300 };
    },
  },
  {
    pattern: "/passwords/revealresult",
    respond: (body) => {
      const r = reveals[body?.revealId];
      if (!r) return { status: "expired" };
      if (r.status !== "pending") return { status: r.status };
      r.polls += 1;
      if (r.polls <= 2) return { status: "pending" };
      r.status = "shown";
      const a = accounts.find((x) => x.endpointId === r.endpointId && x.account === r.account);
      const p = a && profiles.find((x) => x.id === a.policyId);
      let rerotateAt;
      if (a) {
        a.revealedAt = new Date().toISOString();
        a.revealedBy = 4;
        if (p) {
          rerotateAt = ahead(p.rerotateAfterRevealMinutes * MIN);
          a.rerotateAt = rerotateAt;
        }
      }
      return {
        status: "approved",
        password: "mock-Pw7#q2Lr!xV9tEb4Kz", // a fake, for local mode only
        unconfirmed: "mock-Un3$h8Wd^mR6pYa1Jc",
        showSeconds: 60,
        ...(rerotateAt ? { rerotateAt } : {}),
      };
    },
  },
  {
    pattern: "/passwords/setcredential",
    respond: (body) => {
      const a = accounts.find((x) => x.endpointId === Number(body?.endpointId) && x.account === body?.account);
      if (!a) return { inUse: "key" };
      a.credentialOverride = ["key", "password"].includes(body?.credential) ? body.credential : "";
      return { inUse: derive(a).inUse };
    },
  },
  {
    pattern: "/instances/passwordssh",
    respond: (body) => {
      const s = passwordSshOf(body?.instanceId);
      return { state: s.state, allow: [...s.allow], needsSetup: s.needsSetup };
    },
  },
  {
    pattern: "/instances/setpasswordssh",
    respond: (body) => {
      const id = Number(body?.instanceId);
      const allow = [...new Set((body?.allow || []).map((a) => String(a).trim()).filter(Boolean))];
      const cur = passwordSshOf(id);
      PASSWORD_SSH[id] = {
        ...cur,
        allow,
        state: cur.needsSetup ? cur.state : allow.length ? `gateway-only, except ${allow.join(", ")}` : "gateway-only",
      };
      return { allow, scanJobId: "job-x" };
    },
  },
];
